<template>
  <v-menu
      v-model="menu"
      :close-on-content-click="false"
      :nudge-right="40"
      transition="scale-transition"
      offset-y
      min-width="290px"
  >
    <template v-slot:activator="{ props }">
      <v-text-field
          :class="componentClass"
          v-bind="props"
          :label="label || 'Date'"
          :rules="rules"
          :modelValue="dateFormatted"
          :prepend-inner-icon="mdiClockOutline"
          readonly
          @click.stop="openMenu"
      />
    </template>

    <vue-date-picker
        time-picker
        inline
        :modelValue="modelValue"
        :model-type="`timestamp`"
        :minutes-increment="minutesIncrement"
        :minutes-grid-increment="minutesGridIncrement"
        :filters="timeFilters"
        :start-time="startTime"
        @update:modelValue="updateTime"
    >
      <template #action-row="{ selectDate }">
        <v-container class="action-row">
          <v-row class="d-flex justify-center">
          <v-btn size="small" @click="selectDate">OK</v-btn>
          </v-row>
        </v-container>
      </template>
    </vue-date-picker>
  </v-menu>
</template>

<script setup>
import { ref, computed, watchEffect, toRef, nextTick } from 'vue'
import {useI18n} from "vue-i18n";
import moment from 'moment';
import {mdiClockOutline} from '@mdi/js';

const props = defineProps({
  modelValue: {},
  label: {
    type: String,
    default: ''
  },
  componentClass: {
    type: String,
    default: ''
  },
  rules: {
    default: null
  },
  minutesIncrement: {
    type: Number,
    default: 1
  },
  minutesGridIncrement: {
    type: Number,
    default: 1
  }
});

const emit = defineEmits(['update:modelValue'])

const modelValue = toRef(props, 'modelValue');
const { t } = useI18n();

const menu = ref(false);
const input = ref(null);

const timeFilters = computed(() => {
  if (props.minutesGridIncrement <= 1) return undefined;
  const disabledMinutes = [];
  for (let i = 0; i < 60; i++) {
    if (i % props.minutesGridIncrement !== 0) {
      disabledMinutes.push(i);
    }
  }
  return { times: { minutes: disabledMinutes } };
});

const startTime = computed(() => {
  if (props.minutesGridIncrement <= 1) return undefined;
  const now = new Date();
  const minutes = now.getMinutes();
  const rounded = Math.round(minutes / props.minutesGridIncrement) * props.minutesGridIncrement;
  return {
    hours: rounded >= 60 ? now.getHours() + 1 : now.getHours(),
    minutes: rounded >= 60 ? 0 : rounded,
  };
});

function updateTime(val) {
  if (val && props.minutesGridIncrement > 1) {
    const date = new Date(val);
    const minutes = date.getMinutes();
    const rounded = Math.round(minutes / props.minutesGridIncrement) * props.minutesGridIncrement;
    if (rounded !== minutes) {
      date.setMinutes(rounded >= 60 ? 0 : rounded);
      if (rounded >= 60) {
        date.setHours(date.getHours() + 1);
      }
      date.setSeconds(0);
      date.setMilliseconds(0);
      val = date.getTime();
    }
  }
  input.value = val;
  emit('update:modelValue', val);
  menu.value = false;
}
function openMenu() {
  menu.value = true;
  nextTick(() => {
    const activeElement = document.activeElement;
    if (activeElement) {
      activeElement.blur();
    }
  });
}

const dateFormatted = computed(() => {
  return input.value ? moment(input.value).format(localTimeFormat) : '';
});

const localTimeFormat = t('global.locale.timePattern');

watchEffect(() => {
  updateTime(props.modelValue);
});
</script>
